import { parseMoneyInput } from '@/domain/store-setup/money';
import {
  IPriceForm,
  ISizeRowForm,
  MAX_SIZES,
  newSizeRow,
} from './product-form';

/**
 * The «Precio» section's edits (plan §5.12), pure: switching between one
 * price and sizes, moving size rows, and the «Antes $ 21.300 · 13 % menos»
 * line of a discount.
 */

type TSingle = Extract<IPriceForm, { mode: 'single' }>;
type TSizes = Extract<IPriceForm, { mode: 'sizes' }>;

/** «Usar precios múltiples» on one price: that price becomes the first size. */
export function toSizes(price: TSingle, title: string): TSizes {
  return {
    mode: 'sizes',
    title,
    rows: [
      newSizeRow({
        priceText: price.priceText,
        discountText: price.discountText,
      }),
      newSizeRow(),
    ],
  };
}

/** The size whose price stays when going back to one price: the first active one. */
export function keptSize(price: TSizes): ISizeRowForm | null {
  return price.rows.find((row) => row.active) ?? price.rows[0] ?? null;
}

/** «Volver a un solo precio». */
export function toSingle(price: TSizes): TSingle {
  const kept = keptSize(price);
  return {
    mode: 'single',
    priceText: kept?.priceText ?? '',
    discountText: kept?.discountText ?? null,
  };
}

export function canAddSize(price: TSizes): boolean {
  return price.rows.length < MAX_SIZES;
}

export function addSize(price: TSizes): TSizes {
  return canAddSize(price)
    ? { ...price, rows: [...price.rows, newSizeRow()] }
    : price;
}

export function updateSize(
  price: TSizes,
  key: string,
  patch: Partial<ISizeRowForm>
): TSizes {
  return {
    ...price,
    rows: price.rows.map((row) =>
      row.key === key ? { ...row, ...patch } : row
    ),
  };
}

export function removeSize(price: TSizes, key: string): TSizes {
  return { ...price, rows: price.rows.filter((row) => row.key !== key) };
}

/** Moves a row one place up (-1) or down (+1); unchanged at the edges. */
export function moveSize(price: TSizes, key: string, delta: -1 | 1): TSizes {
  const index = price.rows.findIndex((row) => row.key === key);
  const target = index + delta;
  if (index < 0 || target < 0 || target >= price.rows.length) return price;
  const rows = [...price.rows];
  [rows[index], rows[target]] = [rows[target], rows[index]];
  return { ...price, rows };
}

export interface IDiscountSummary {
  /** The price before the discount. */
  before: number;
  /** Whole percent off (13 for 13 %). */
  percent: number;
}

/** «Antes $ 21.300 · 13 % menos»; null while the amounts are incomplete. */
export function discountSummary(
  priceText: string,
  discountText: string | null
): IDiscountSummary | null {
  if (discountText === null) return null;
  const price = parseMoneyInput(priceText, { allowZero: true });
  const discount = parseMoneyInput(discountText, { allowZero: true });
  if (price === null || discount === null || price <= 0 || discount >= price)
    return null;
  return {
    before: price,
    percent: Math.round(((price - discount) / price) * 100),
  };
}
